import {useEffect, useRef} from 'react';

import {FilterConfigT} from '@/lib/types/filter.types';
import {IndexedObjectT, NestedKeyOfT} from '@/lib/types/utility.types';

import {Modal, ModalRefT} from '@/components/Modal';

export type MapStructureT<T> = {
  displayFields: {
    title: string;
    field: NestedKeyOfT<T>;
    type?: string;
    isImage?: boolean;
  }[];
  valueField: NestedKeyOfT<T>;
};

type SearchAndSelectPropsT<T> = {
  isVisible: boolean;
  onClose: () => void;
  onSelect: (item: T) => void;
  onSearch: (searchValue: string) => void;
  results: T[];
  title: string;
  mapStructure: MapStructureT<T>;
  error?: string;
  additionalAction?: (item: T) => void;
  additionalActionLabel?: string;
  overrideClose?: boolean;

  filterConfig?: FilterConfigT[];
  onFilterChange?: (field: string, value: string) => void;
  onCreateNew?: () => void;
  createNewLabel?: string;
};

const getNestedValue = <T,>(obj: T, path: string) => {
  return String(
    path.split('.').reduce((acc: IndexedObjectT | undefined, part: string) => acc && acc[part], obj as IndexedObjectT)
  );
};

/**
 * A search and select component that can be used to search for items and select them
 * @param {boolean} isVisible - Whether the component is visible or not
 * @param {function} onClose - Function to close the component
 * @param {function} onSelect - Function to call when an item is selected
 * @param {function} onSearch - Function to call when the search input changes
 * @param {array} results - The search results
 * @param {string} title - The title of the component
 * @param {MapStructureT<T>} mapStructure - The structure of the search results
 * @param {string} error - The error message to display
 * @param {function} additionalAction - Function to call when an additional action is clicked
 * @param {string} additionalActionLabel - The label of the additional action button
 * @returns {JSX.Element}
 */
export const SearchAndSelectComponent = <T,>({
  isVisible,
  onClose,
  onSelect,
  onSearch,
  results,
  title,
  mapStructure,
  additionalAction,
  additionalActionLabel = 'Action',
  overrideClose = false,
  filterConfig,
  onFilterChange,
  onCreateNew,
  createNewLabel = 'Create New',
}: SearchAndSelectPropsT<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<ModalRefT>(null);

  const handleSelect = (item: T) => {
    onSelect(item);
    if (!overrideClose) {
      modalRef.current?.hide();
      onClose();
    }
    if (inputRef.current) inputRef.current.value = '';
    onSearch('');
  };

  const handleClose = () => {
    if (inputRef.current) inputRef.current.value = '';
    modalRef.current?.hide();
    onSearch('');
    onClose();
  };

  // Show/hide modal based on isVisible prop
  useEffect(() => {
    if (isVisible) {
      modalRef.current?.show();
      // Reset search when opening
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      onSearch('');
    } else {
      modalRef.current?.hide();
    }
  }, [isVisible, onSearch]);

  return (
    <Modal ref={modalRef} title={title} onClose={handleClose} size="xl">
      <div className="grid grid-cols-12 gap-4 px-6">
        <div className="col-span-12">
          <div className="relative">
            <input
              ref={inputRef}
              onChange={e => onSearch(e.target.value)}
              type="text"
              placeholder="Search..."
              className="form-control w-full !rounded-md pr-10"
            />
            {inputRef.current?.value && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 hover:bg-gray-100"
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.value = '';
                  }
                  onSearch('');
                }}
              >
                <i className="ti ti-x text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Section */}
        {filterConfig && filterConfig.length > 0 && (
          <div className="col-span-12 space-y-2">
            <div className="flex flex-wrap gap-2">
              {filterConfig.map(filter => (
                <div key={filter.field} className="min-w-[150px] flex-1">
                  <label className="mb-1 block text-sm font-medium text-gray-700">{filter.label}</label>
                  {filter.type === 'dropdown' && (
                    <select
                      className="form-select w-full !rounded-md"
                      defaultValue={String(filter.currentValue)}
                      onChange={e => onFilterChange?.(filter.field, e.target.value)}
                    >
                      <option value="">All</option>
                      {filter.options?.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 flex max-h-[50vh] flex-col overflow-y-auto px-6">
        {results?.length === 0 && <div className="text-muted text-center">No results found!</div>}
        {results?.map((result, index) => {
          // Calculate dynamic height based on number of display fields
          const numFields = mapStructure.displayFields.filter(field => !field.isImage).length;
          const minHeight = Math.max(4, numFields * 1.5); // 1.5rem per field, minimum 4rem

          return (
            <div
              key={index}
              className="flex items-center justify-between border-b border-gray-700 py-2 pr-2"
              style={{minHeight: `${minHeight}rem`}}
            >
              <div className="flex min-w-0 flex-1 items-center gap-4 pr-4">
                {mapStructure.displayFields.some(field => field.isImage) && (
                  <div className="h-12 w-12 flex-shrink-0">
                    <img
                      src={getNestedValue(result, mapStructure.displayFields.find(field => field.isImage)?.field || '')}
                      alt="Item"
                      className="h-full w-full rounded-md object-cover"
                    />
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col justify-center space-y-1">
                  {mapStructure.displayFields
                    .filter(field => !field.isImage)
                    .map(({title, type, field}, innerIndex) => (
                      <div key={innerIndex} className="truncate">
                        <span className="font-semibold">{title}: </span>
                        <span className="text-muted font-normal">
                          {type === 'currency' ? '$' : ''}
                          {getNestedValue(result, field)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="flex flex-shrink-0 space-x-2">
                {additionalAction && (
                  <button onClick={() => additionalAction(result)} className="ti-btn bg-secondary text-white">
                    {additionalActionLabel}
                  </button>
                )}
                <button type="button" onClick={() => handleSelect(result)} className="ti-btn bg-primary text-white">
                  Select
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {onCreateNew && (
        <div className="mt-4 flex justify-end space-x-2 px-6 pb-4">
          <button onClick={onCreateNew} className="ti-btn bg-secondary text-white">
            {createNewLabel}
          </button>
        </div>
      )}
    </Modal>
  );
};
